/**
 * Quick View Modal
 * Simple modal that fetches and displays product content with thumbnail gallery
 */
window.QuickView = (() => {
  if (typeof window.QuickView !== 'undefined') {
    return window.QuickView;
  }

  const selectors = {
    modal: '#quick-view-modal',
    overlay: '.js-quick-view-overlay',
    closeButton: '.js-quick-view-close',
    content: '.js-quick-view-content',
    quickViewButton: '.js-quick-view-button',
    productCard: '.card-product',
  };

  let currentProductUrl = null;
  let currentProductHandle = null;
  const cachedContent = new Map();
  const cachedImages = new Map();
  const prefetchedUrls = new Set();

  /**
   * Extracts product handle from URL
   * @param {string} url - Product URL
   * @returns {string|null} Product handle
   */
  const extractHandleFromUrl = (url) => {
    try {
      const urlObj = new URL(url, window.location.origin);
      const pathParts = urlObj.pathname.split('/').filter(Boolean);
      const productsIndex = pathParts.indexOf('products');
      if (productsIndex !== -1 && pathParts[productsIndex + 1]) {
        return pathParts[productsIndex + 1];
      }
    } catch (e) {
      console.error('Error extracting handle from URL:', e);
    }
    return null;
  };

  /**
   * Opens the quick view modal
   */
  const openModal = () => {
    const modal = document.querySelector(selectors.modal);
    if (!modal) return;

    modal.style.display = '';
    modal.classList.add('is-open');
    modal.setAttribute('aria-hidden', 'false');
    document.body.style.overflow = 'hidden';

    const closeButton = modal.querySelector(selectors.closeButton);
    if (closeButton) {
      closeButton.focus();
    }
  };

  /**
   * Closes the quick view modal
   */
  const closeModal = () => {
    const modal = document.querySelector(selectors.modal);
    if (!modal) return;

    modal.classList.remove('is-open');
    modal.setAttribute('aria-hidden', 'true');
    document.body.style.overflow = '';

    currentProductUrl = null;
    currentProductHandle = null;
  };

  /**
   * Shows loading state in the modal
   */
  const showLoading = () => {
    const content = document.querySelector(selectors.content);
    if (!content) return;

    content.innerHTML = `
      <div class="quick-view-modal__loading">
        <div class="quick-view-loading-spinner">
          <svg class="spinner" viewBox="0 0 50 50">
            <circle class="path" cx="25" cy="25" r="20" fill="none" stroke-width="4"></circle>
          </svg>
        </div>
      </div>
    `;
  };

  /**
   * Fetches the product page
   * @param {string} productUrl - The product URL
   * @returns {Promise<Document|null>} - The parsed HTML document
   */
  const fetchProductPage = async (productUrl) => {
    try {
      const url = new URL(productUrl, window.location.origin);
      const response = await fetch(url.href);

      if (!response.ok) {
        throw new Error(`HTTP error: ${response.status}`);
      }

      const html = await response.text();
      const parser = new DOMParser();
      return parser.parseFromString(html, 'text/html');
    } catch (error) {
      console.error('Error fetching product page:', error);
      return null;
    }
  };

  /**
   * Prefetches product data on hover
   * @param {string} productUrl - The product URL to prefetch
   */
  const prefetchProduct = async (productUrl) => {
    if (!productUrl || cachedContent.has(productUrl) || prefetchedUrls.has(productUrl)) {
      return;
    }

    prefetchedUrls.add(productUrl);

    const doc = await fetchProductPage(productUrl);
    if (doc) {
      const images = extractImages(doc);
      const content = extractProductContent(doc);
      if (content) {
        cachedContent.set(productUrl, content);
        cachedImages.set(productUrl, images);
      }
    }
  };

  /**
   * Extracts images from the product page
   * @param {Document} doc - The parsed HTML document
   * @returns {Array} - Array of image objects
   */
  const extractImages = (doc) => {
    const images = [];
    
    // Try to get images from the JSON data
    const jsonScript = doc.querySelector('#product-images-json');
    if (jsonScript) {
      try {
        const data = JSON.parse(jsonScript.textContent);
        if (data.images && data.images.length > 0) {
          data.images.forEach((img, index) => {
            images.push({
              src: img.src.replace(/width=\d+/, 'width=800'),
              thumb: img.src.replace(/width=\d+/, 'width=100'),
              alt: img.alt || `Product image ${index + 1}`
            });
          });
          return images;
        }
      } catch (e) {
        console.warn('Could not parse product images JSON');
      }
    }

    // Fallback: extract from gallery
    const galleryImages = doc.querySelectorAll('.product-media-gallery img, .product__media-item img');
    galleryImages.forEach((img, index) => {
      const src = img.src || img.dataset.src;
      if (src && !images.find(i => i.src === src)) {
        images.push({
          src: src.replace(/width=\d+/, 'width=800'),
          thumb: src.replace(/width=\d+/, 'width=100'),
          alt: img.alt || `Product image ${index + 1}`
        });
      }
    });

    return images;
  };

  /**
   * Creates the custom gallery HTML
   * @param {Array} images - Array of image objects
   * @returns {string} - Gallery HTML
   */
  const createGalleryHtml = (images) => {
    if (!images || images.length === 0) {
      return '<div class="quick-view-gallery__main"><p>No images available</p></div>';
    }

    const mainImage = images[0];
    const thumbsHtml = images.map((img, index) => `
      <button class="quick-view-gallery__thumb${index === 0 ? ' active' : ''}" data-index="${index}" type="button">
        <img src="${img.thumb}" alt="${img.alt}" loading="lazy" />
      </button>
    `).join('');

    return `
      <div class="quick-view-gallery">
        <div class="quick-view-gallery__main">
          ${images.length > 1 ? `
            <button class="quick-view-gallery__nav quick-view-gallery__nav--prev" type="button" disabled>
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                <path fill-rule="evenodd" clip-rule="evenodd" d="M16.75 6.99998L9.24998 12L16.75 17C16.8485 17.0656 16.9331 17.1501 16.9989 17.2484C17.0648 17.3468 17.1106 17.4571 17.1339 17.5732C17.1571 17.6893 17.1572 17.8088 17.1342 17.9249C17.1113 18.041 17.0656 18.1515 17 18.25C16.8674 18.4489 16.6612 18.587 16.4268 18.6339C16.3107 18.6571 16.1912 18.6572 16.0751 18.6342C15.9589 18.6113 15.8485 18.5656 15.75 18.5L7.24798 12.832C7.11102 12.7407 6.99873 12.6169 6.92106 12.4718C6.84339 12.3267 6.80276 12.1646 6.80276 12C6.80276 11.8354 6.84339 11.6733 6.92106 11.5282C6.99873 11.383 7.11102 11.2593 7.24798 11.168L15.75 5.49998C15.8485 5.43432 15.9589 5.3887 16.0751 5.36573C16.1912 5.34276 16.3107 5.34288 16.4268 5.36609C16.5428 5.38931 16.6532 5.43516 16.7515 5.50103C16.8499 5.56689 16.9343 5.65149 17 5.74998C17.0656 5.84847 17.1113 5.95893 17.1342 6.07505C17.1572 6.19117 17.1571 6.31068 17.1339 6.42676C17.1106 6.54283 17.0648 6.65319 16.9989 6.75155C16.9331 6.8499 16.8485 6.93432 16.75 6.99998Z" fill="black"></path>
              </svg>
            </button>
            <button class="quick-view-gallery__nav quick-view-gallery__nav--next" type="button">
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                <path fill-rule="evenodd" clip-rule="evenodd" d="M16.75 6.99998L9.24998 12L16.75 17C16.8485 17.0656 16.9331 17.1501 16.9989 17.2484C17.0648 17.3468 17.1106 17.4571 17.1339 17.5732C17.1571 17.6893 17.1572 17.8088 17.1342 17.9249C17.1113 18.041 17.0656 18.1515 17 18.25C16.8674 18.4489 16.6612 18.587 16.4268 18.6339C16.3107 18.6571 16.1912 18.6572 16.0751 18.6342C15.9589 18.6113 15.8485 18.5656 15.75 18.5L7.24798 12.832C7.11102 12.7407 6.99873 12.6169 6.92106 12.4718C6.84339 12.3267 6.80276 12.1646 6.80276 12C6.80276 11.8354 6.84339 11.6733 6.92106 11.5282C6.99873 11.383 7.11102 11.2593 7.24798 11.168L15.75 5.49998C15.8485 5.43432 15.9589 5.3887 16.0751 5.36573C16.1912 5.34276 16.3107 5.34288 16.4268 5.36609C16.5428 5.38931 16.6532 5.43516 16.7515 5.50103C16.8499 5.56689 16.9343 5.65149 17 5.74998C17.0656 5.84847 17.1113 5.95893 17.1342 6.07505C17.1572 6.19117 17.1571 6.31068 17.1339 6.42676C17.1106 6.54283 17.0648 6.65319 16.9989 6.75155C16.9331 6.8499 16.8485 6.93432 16.75 6.99998Z" fill="black"></path>
              </svg>
            </button>
          ` : ''}
          <img src="${mainImage.src}" alt="${mainImage.alt}" />
        </div>
        ${images.length > 1 ? `
          <div class="quick-view-gallery__thumbs-wrapper">
            <div class="quick-view-gallery__thumbs">
              ${thumbsHtml}
            </div>
          </div>
        ` : ''}
      </div>
    `;
  };

  /**
   * Extracts product content from the fetched page
   * @param {Document} doc - The parsed HTML document
   * @returns {string|null} - The extracted HTML content
   */
  const extractProductContent = (doc) => {
    const productInfo = doc.querySelector('product-info');
    if (productInfo) {
      const clone = productInfo.cloneNode(true);
      
      // Remove elements that shouldn't appear in quick view
      const elementsToRemove = clone.querySelectorAll(
        '.breadcrumb-section, .breadcrumb-metafield, .breadcrumbs, .breadcrumb, .product__view-details, .js-bogos-bundle-holder, .quick-add-hidden, .product-popup-modal, .complementary-products, .service-info, .delivery-info, .click-and-collect-wrapper, .product__accordion, .product__description, .product-features, .share-button, quantity-input, .quantity, .product-form__quantity, .product-media-gallery'
      );
      elementsToRemove.forEach(el => el.remove());

      // Prevent URL updates
      clone.setAttribute('data-update-url', 'false');

      return clone.outerHTML;
    }

    const mainProduct = doc.querySelector('.main-product');
    if (mainProduct) {
      const clone = mainProduct.cloneNode(true);
      
      const elementsToRemove = clone.querySelectorAll(
        '.breadcrumb-section, .breadcrumb-metafield, .breadcrumbs, .breadcrumb, .product__view-details, .js-bogos-bundle-holder, .quick-add-hidden, quantity-input, .quantity, .product-media-gallery'
      );
      elementsToRemove.forEach(el => el.remove());

      return clone.outerHTML;
    }

    return null;
  };

  /**
   * Syncs the gallery to show the featured image of the currently selected variant
   * @param {HTMLElement} content - The content container
   */
  const syncGalleryWithSelectedVariant = (content) => {
    // Find the first selected variant option (could be dropdown or radio)
    const firstSelectedOption = content.querySelector('variant-selects select option[selected], variant-selects input[type="radio"]:checked');
    
    if (!firstSelectedOption) return;

    // Get the input ID to find the variant data
    let inputId;
    if (firstSelectedOption.tagName === 'OPTION') {
      inputId = firstSelectedOption.id;
    } else {
      inputId = firstSelectedOption.id;
    }

    if (!inputId) return;

    // Find the variant data script tag
    const scriptTag = content.querySelector(`script[data-resource="${inputId}"]`);
    if (!scriptTag) return;

    try {
      const variantData = JSON.parse(scriptTag.textContent);
      if (variantData?.featured_image?.src) {
        // Use setTimeout to ensure gallery is fully initialized
        setTimeout(() => {
          switchGalleryImage(variantData.featured_image.src);
        }, 100);
      }
    } catch (e) {
      // Variant data unavailable or parsing failed
      console.warn('Could not sync gallery with selected variant:', e);
    }
  };

  /**
   * Initializes the content after HTML injection
   * @param {HTMLElement} content - The content container
   */
  const initializeCustomElements = (content) => {
    // Initialize the variant change handler (using Section Rendering API)
    QuickViewVariantHandler.init(content, currentProductUrl);
    
    // Sync gallery with the initially selected variant
    syncGalleryWithSelectedVariant(content);
  };

  /**
   * Intercepts form submissions inside the quick view modal so add-to-cart uses
   * the same fetch + cart-drawer sections flow as elsewhere.
   */
  const setupFormInterceptor = () => {
    const contentEl = document.querySelector(selectors.content);
    if (!contentEl) return;

    contentEl.addEventListener('submit', (evt) => {
      const form = evt.target.closest ? evt.target.closest('form') : evt.target;
      if (!form || !form.action || !form.action.includes('/cart/add')) return;

      evt.preventDefault();
      evt.stopPropagation();

      const submitButton = form.querySelector('[type="submit"]');
      if (submitButton?.getAttribute('aria-disabled') === 'true') return;

      if (submitButton) {
        submitButton.setAttribute('aria-disabled', 'true');
        submitButton.classList.add('loading');
      }
      const spinner = form.querySelector('.loading__spinner');
      if (spinner) spinner.classList.remove('hidden');

      const config = fetchConfig('javascript');
      config.headers['X-Requested-With'] = 'XMLHttpRequest';
      delete config.headers['Content-Type'];

      const formData = new FormData(form);
      const cartDrawer = document.querySelector('cart-drawer');

      if (cartDrawer) {
        formData.append(
          'sections',
          cartDrawer.getSectionsToRender().map((section) => section.id)
        );
        formData.append('sections_url', window.location.pathname);
        cartDrawer.setActiveElement(document.activeElement);
      }
      config.body = formData;

      fetch(`${routes.cart_add_url}`, config)
        .then((response) => response.json())
        .then((response) => {
          if (response.status) {
            if (typeof publish !== 'undefined' && typeof PUB_SUB_EVENTS !== 'undefined') {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'quick-view',
                productVariantId: formData.get('id'),
                errors: response.errors || response.description,
                message: response.message,
              });
            }
            const errorWrapper = form.querySelector('.product-form__error-message-wrapper');
            const errorMsg = errorWrapper?.querySelector('.product-form__error-message');
            if (errorWrapper && errorMsg) {
              errorWrapper.removeAttribute('hidden');
              errorMsg.textContent = response.description;
            }
            return;
          }

          if (typeof publish !== 'undefined' && typeof PUB_SUB_EVENTS !== 'undefined') {
            publish(PUB_SUB_EVENTS.cartUpdate, {
              source: 'quick-view',
              productVariantId: formData.get('id'),
              cartData: response,
            });
          }

          if (cartDrawer) {
            cartDrawer.renderContents(response);
          }

          console.log('product has been added to the cart', response);
        })
        .catch((e) => {
          console.error('Quick view add to cart error:', e);
        })
        .finally(() => {
          if (submitButton) {
            submitButton.classList.remove('loading');
            submitButton.removeAttribute('aria-disabled');
          }
          if (spinner) spinner.classList.add('hidden');
        });
    }, true);
  };

  /**
   * Updates the modal content with the product data
   * @param {string} htmlContent - The HTML content to display
   * @param {Array} images - Array of image objects
   */
  const updateModalContent = (htmlContent, images) => {
    const content = document.querySelector(selectors.content);
    if (!content) return;

    // Set fresh HTML content
    content.innerHTML = htmlContent;

    // Add custom gallery to media wrapper
    const mediaWrapper = content.querySelector('.product__media-wrapper');
    if (mediaWrapper && images && images.length > 0) {
      mediaWrapper.innerHTML = createGalleryHtml(images);
      initializeGallery();
    }

    // Move quantity_buy_and_points outside product__info-wrapper to product grid
    // This is safe to do each time since we're working with fresh HTML from innerHTML
    const buyButtonWrapper = content.querySelector('.quantity_buy_and_points');
    const productGrid = content.querySelector('.main-product .product');
    if (buyButtonWrapper && productGrid) {
      // Only move if it's still inside info-wrapper (to avoid double-moving)
      const infoWrapper = content.querySelector('.product__info-wrapper');
      if (infoWrapper && infoWrapper.contains(buyButtonWrapper)) {
        productGrid.appendChild(buyButtonWrapper);
      }
    }

    // Add "mehr details" link - insert after variant picker if exists, otherwise append to info container
    if (currentProductUrl && !content.querySelector('.quick-view-more-details-link')) {
      const detailsLink = document.createElement('a');
      detailsLink.href = currentProductUrl;
      detailsLink.className = 'quick-view-more-details-link';
      detailsLink.textContent = 'mehr details';

      // Try to insert after variant picker wrapper (if product has variants)
      const variantPickerWrapper = content.querySelector('.main-product__variant_picker-wrapper');
      if (variantPickerWrapper) {
        variantPickerWrapper.insertAdjacentElement('afterend', detailsLink);
      } else {
        // For products without variants, append to the product info container
        const productInfoContainer = content.querySelector('.product__info-container');
        if (productInfoContainer) {
          productInfoContainer.appendChild(detailsLink);
        }
      }
    }

    // Update review brand link to point to product page instead of collection
    updateReviewBrandLink(content, currentProductUrl);

    // Initialize custom elements after content injection
    initializeCustomElements(content);

    // Dispatch event for other components
    document.dispatchEvent(new CustomEvent('quick-view:loaded', {
      detail: { content },
    }));
  };

  /**
   * Initializes the gallery functionality
   */
  const initializeGallery = () => {
    const content = document.querySelector(selectors.content);
    if (!content) return;

    const gallery = content.querySelector('.quick-view-gallery');
    if (!gallery) return;

    const mainImg = gallery.querySelector('.quick-view-gallery__main img');
    const thumbsContainer = gallery.querySelector('.quick-view-gallery__thumbs');
    const thumbs = gallery.querySelectorAll('.quick-view-gallery__thumb');
    const prevBtn = gallery.querySelector('.quick-view-gallery__nav--prev');
    const nextBtn = gallery.querySelector('.quick-view-gallery__nav--next');
    
    let currentIndex = 0;
    const totalImages = thumbs.length;
    // const thumbWidth = 64; // thumb width
    const thumbGap = 8; // gap between thumbs
    // const visibleThumbs = 3; // number of visible thumbnails
    const visibleThumbs = 2.5;

    const thumbsWrapper = gallery.querySelector('.quick-view-gallery__thumbs-wrapper');
    const wrapperWidth = thumbsWrapper ? thumbsWrapper.clientWidth : 200;
    const thumbWidth = (wrapperWidth - (Math.ceil(visibleThumbs) - 1) * thumbGap) / visibleThumbs;

    thumbs.forEach((thumb) => {
      thumb.style.width = thumbWidth + 'px';
      thumb.style.height = thumbWidth + 'px';
    });

    const updateGallery = (index) => {
      if (index < 0 || index >= totalImages) return;
      
      currentIndex = index;
      const thumb = thumbs[index];
      const thumbImg = thumb.querySelector('img');
      
      // Update main image with fade effect
      const newSrc = thumbImg.src.replace(/width=\d+/, 'width=800');
      mainImg.style.opacity = '0';
      
      setTimeout(() => {
        mainImg.src = newSrc;
        mainImg.alt = thumbImg.alt;
        mainImg.style.opacity = '1';
      }, 150);

      // Update active state
      thumbs.forEach(t => t.classList.remove('active'));
      thumb.classList.add('active');

      // Update nav buttons
      if (prevBtn) prevBtn.disabled = index === 0;
      if (nextBtn) nextBtn.disabled = index === totalImages - 1;

      // Slide thumbnails to keep active one visible (show 3 at a time)
      if (thumbsContainer && totalImages > visibleThumbs) {
        // Calculate which "page" of thumbs to show
        // Keep the active thumb in the middle position when possible
        let slideIndex = Math.max(0, index - 1);
        slideIndex = Math.min(slideIndex, totalImages - visibleThumbs);
        
        const translateX = slideIndex * (thumbWidth + thumbGap);
        thumbsContainer.style.transform = `translateX(-${translateX}px)`;
      }
    };

    // Thumb click handlers
    thumbs.forEach((thumb, index) => {
      thumb.addEventListener('click', () => updateGallery(index));
    });

    // Nav button handlers
    if (prevBtn) {
      prevBtn.addEventListener('click', () => updateGallery(currentIndex - 1));
    }
    if (nextBtn) {
      nextBtn.addEventListener('click', () => updateGallery(currentIndex + 1));
    }

    // Keyboard navigation
    gallery.addEventListener('keydown', (e) => {
      if (e.key === 'ArrowLeft') updateGallery(currentIndex - 1);
      if (e.key === 'ArrowRight') updateGallery(currentIndex + 1);
    });
  };

  /**
   * Switches the gallery to a specific image by src
   * @param {string} imageSrc - The image source to switch to
   */
  const switchGalleryImage = (imageSrc) => {
    const content = document.querySelector(selectors.content);
    if (!content) return;

    const normalizeImageUrl = (url) => {
      if (!url) return '';
      try {
        const urlObj = new URL(url, 'https://placeholder.com');
        return urlObj.pathname;
      } catch {
        return url.split('?')[0];
      }
    };

    const normalizedSrc = normalizeImageUrl(imageSrc);
    const thumbs = content.querySelectorAll('.quick-view-gallery__thumb');

    for (const thumb of thumbs) {
      const thumbImg = thumb.querySelector('img');
      if (thumbImg && normalizeImageUrl(thumbImg.src) === normalizedSrc) {
        thumb.click();
        return;
      }
    }
  };

  /**
   * Updates the review brand link to point to the product page
   * @param {HTMLElement} content - The content container
   * @param {string} productUrl - The product page URL
   */
  const updateReviewBrandLink = (content, productUrl) => {
    if (!content || !productUrl) return;

    const reviewBrandLink = content.querySelector('.review-section__brand');
    if (reviewBrandLink) {
      reviewBrandLink.href = productUrl;
    }
  };

  /**
   * Shows error state in the modal
   * @param {string} message - Error message to display
   */
  const showError = (message) => {
    const content = document.querySelector(selectors.content);
    if (!content) return;

    content.innerHTML = `
      <div class="quick-view-modal__error">
        <p>${message}</p>
        <button type="button" class="quick-view-modal__retry js-quick-view-retry">
          Try Again
        </button>
      </div>
    `;
  };

  /**
   * Handles the quick view button click
   * @param {HTMLElement} button - The clicked button element
   */
  const handleQuickViewClick = async (button) => {
    const productUrl = button.dataset.productUrl;
    const productHandle = button.dataset.productHandle;

    if (!productUrl) {
      console.error('Quick view: No product URL provided');
      return;
    }

    currentProductUrl = productUrl;
    currentProductHandle = productHandle || extractHandleFromUrl(productUrl);

    // Check if content is already cached from prefetch
    if (cachedContent.has(productUrl) && cachedImages.has(productUrl)) {
      openModal();
      updateModalContent(cachedContent.get(productUrl), cachedImages.get(productUrl));
      return;
    }

    openModal();
    showLoading();

    // Fetch the product page
    const doc = await fetchProductPage(productUrl);
    if (!doc) {
      showError('Failed to load product. Please try again.');
      return;
    }

    // Extract images and content
    const images = extractImages(doc);
    const content = extractProductContent(doc);
    
    if (content) {
      cachedContent.set(productUrl, content);
      cachedImages.set(productUrl, images);
      updateModalContent(content, images);
    } else {
      showError('Could not load product details.');
    }
  };

  /**
   * Handles keyboard events
   * @param {KeyboardEvent} event - The keyboard event
   */
  const handleKeyDown = (event) => {
    if (event.key === 'Escape') {
      const modal = document.querySelector(selectors.modal);
      if (modal && modal.classList.contains('is-open')) {
        closeModal();
      }
    }
  };

  /**
   * Retries loading the current product
   */
  const retryLoad = async () => {
    if (!currentProductUrl) return;
    
    showLoading();
    cachedContent.delete(currentProductUrl);
    cachedImages.delete(currentProductUrl);
    
    const doc = await fetchProductPage(currentProductUrl);
    if (!doc) {
      showError('Failed to load product. Please try again.');
      return;
    }

    const images = extractImages(doc);
    const content = extractProductContent(doc);
    
    if (content) {
      cachedContent.set(currentProductUrl, content);
      cachedImages.set(currentProductUrl, images);
      updateModalContent(content, images);
    } else {
      showError('Could not load product details.');
    }
  };

  /**
   * Sets up event listeners
   */
  const setupEventListeners = () => {
    // Click events using delegation
    document.addEventListener('click', (event) => {
      const quickViewButton = event.target.closest(selectors.quickViewButton);
      if (quickViewButton) {
        event.preventDefault();
        event.stopPropagation();
        handleQuickViewClick(quickViewButton);
        return;
      }

      if (event.target.closest(selectors.closeButton)) {
        closeModal();
        return;
      }

      if (event.target.closest(selectors.overlay)) {
        closeModal();
        return;
      }

      if (event.target.closest('.js-quick-view-retry')) {
        retryLoad();
        return;
      }
    });

    // Hover prefetch on product cards - NO DELAY for instant prefetching
    document.addEventListener('mouseenter', (event) => {
      // Check if target is an Element (has closest method)
      if (!event.target || typeof event.target.closest !== 'function') {
        return;
      }
      
      const productCard = event.target.closest(selectors.productCard);
      if (productCard) {
        const quickViewBtn = productCard.querySelector(selectors.quickViewButton);
        if (quickViewBtn && quickViewBtn.dataset.productUrl) {
          // Start prefetching immediately
          prefetchProduct(quickViewBtn.dataset.productUrl);
        }
      }
    }, true);

    // Keyboard events
    document.addEventListener('keydown', handleKeyDown);
  };

  /**
   * Clears the content cache
   */
  const clearCache = () => {
    cachedContent.clear();
    cachedImages.clear();
    prefetchedUrls.clear();
  };

  /**
   * Initializes the quick view functionality
   */
  const init = () => {
    setupEventListeners();
    setupFormInterceptor();
  };

  // Initialize when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  return {
    open: openModal,
    close: closeModal,
    clearCache,
    prefetch: prefetchProduct
  };
})();


/* ==========================================================================
  QUICK VIEW VARIANT HANDLER - SECTION RENDERING API
  
  This module handles variant changes in the quick view modal using Shopify's
  Section Rendering API, similar to how the product page handles it.
  
  When a variant is changed:
  1. We build a URL with section_id parameter
  2. Fetch the updated section from Shopify
  3. Update the price, variant ID, and button state from the response
   ========================================================================== */

const QuickViewVariantHandler = (() => {
  let currentProductUrl = null;
  let abortController = null;
  let contentContainer = null;

  /**
   * Initializes the variant handler for the quick view modal
   * @param {HTMLElement} content - The content container
   * @param {string} productUrl - The product URL
   */
  const init = (content, productUrl) => {
    contentContainer = content;
    currentProductUrl = productUrl;

    // Find all variant inputs (selects and radios)
    const variantSelects = content.querySelectorAll('variant-selects select');
    const variantRadios = content.querySelectorAll('variant-selects input[type="radio"]');

    // Attach change listeners
    variantSelects.forEach(select => {
      select.addEventListener('change', handleVariantChange);
    });

    variantRadios.forEach(radio => {
      radio.addEventListener('change', handleVariantChange);
    });

    // Disable Add to Cart if not all options are selected on initial load
    if (!areAllOptionsSelected()) {
      const submitButton = contentContainer?.querySelector('.product-form__submit');
      if (submitButton) {
        submitButton.disabled = true;
      }
    }
  };

  /**
   * Handles variant change event
   * @param {Event} event - The change event
   */
  const handleVariantChange = async (event) => {
    const target = event.target;

    // Immediately disable the add-to-cart button to prevent premature clicks
    const submitButton = contentContainer?.querySelector('.product-form__submit');
    if (submitButton) {
      submitButton.disabled = true;
      submitButton.classList.add('loading');
    }

    // Immediately update gallery image from variant data (before section fetch)
    updateGalleryFromVariant(target);

    // Get the selected option values
    const selectedOptionValues = getSelectedOptionValues();
    if (!selectedOptionValues.length) return;

    // Build the request URL with Section Rendering API
    const requestUrl = buildSectionRenderingUrl(selectedOptionValues);

    // Fetch and update
    await fetchAndUpdateSection(requestUrl);
  };

  /**
   * Gets all selected option values from the variant picker
   * @returns {string[]} Array of selected option value IDs
   */
  const getSelectedOptionValues = () => {
    if (!contentContainer) return [];
    
    const selectedValues = [];
    
    // Get selected values from dropdowns
    contentContainer.querySelectorAll('variant-selects select').forEach(select => {
      const selectedOption = select.options[select.selectedIndex];
      // Skip placeholder options - they don't have optionValueId attribute
      if (selectedOption && selectedOption.dataset.optionValueId) {
        selectedValues.push(selectedOption.dataset.optionValueId);
      }
    });
    
    // Get selected values from radio buttons
    contentContainer.querySelectorAll('variant-selects input[type="radio"]:checked').forEach(radio => {
      if (radio.dataset.optionValueId) {
        selectedValues.push(radio.dataset.optionValueId);
      }
    });
    
    return selectedValues;
  };

  /**
   * Normalizes a Shopify CDN image URL to its pathname for comparison
   * @param {string} url - The image URL
   * @returns {string} Normalized pathname
   */
  const normalizeImageUrl = (url) => {
    if (!url) return '';
    try {
      const urlObj = new URL(url, 'https://placeholder.com');
      return urlObj.pathname;
    } catch {
      return url.split('?')[0];
    }
  };

  /**
   * Reads variant data from the data-resource script tag and updates gallery
   * @param {HTMLElement} inputElement - The changed input (radio or select)
   */
  const updateGalleryFromVariant = (inputElement) => {
    let inputId;
    if (inputElement.tagName === 'SELECT') {
      const selectedOption = inputElement.options[inputElement.selectedIndex];
      inputId = selectedOption?.id;
    } else {
      inputId = inputElement.id;
    }

    if (!inputId) return;

    const scriptTag = contentContainer?.querySelector(`script[data-resource="${inputId}"]`);
    if (!scriptTag) return;

    try {
      const variantData = JSON.parse(scriptTag.textContent);
      if (variantData?.featured_image?.src) {
        switchGalleryToImage(variantData.featured_image.src);
      }
    } catch {
      // Variant data unavailable
    }
  };

  /**
   * Switches the quick view gallery to the image matching the given src
   * @param {string} imageSrc - The featured image src to switch to
   */
  const switchGalleryToImage = (imageSrc) => {
    if (!contentContainer) return;

    const normalizedSrc = normalizeImageUrl(imageSrc);
    const thumbs = contentContainer.querySelectorAll('.quick-view-gallery__thumb');

    for (const thumb of thumbs) {
      const thumbImg = thumb.querySelector('img');
      if (thumbImg && normalizeImageUrl(thumbImg.src) === normalizedSrc) {
        thumb.click();
        return;
      }
    }
  };

  /**
   * Checks whether all variant options have a valid selection
   * @returns {boolean} True if every option group has a real value selected
   */
  const areAllOptionsSelected = () => {
    if (!contentContainer) return false;

    // Check all selects - none should have "Bitte wählen" selected
    const selects = contentContainer.querySelectorAll('variant-selects select');
    for (const select of selects) {
      const selectedOption = select.options[select.selectedIndex];
      if (selectedOption?.id === 'bittewahlen') return false;
      if (!selectedOption?.dataset?.optionValueId) return false;
    }

    // Check radio button groups - each fieldset must have one checked
    const fieldsets = contentContainer.querySelectorAll('variant-selects fieldset');
    for (const fieldset of fieldsets) {
      const checked = fieldset.querySelector('input[type="radio"]:checked');
      if (!checked) return false;
    }

    return true;
  };

  /**
   * Builds the Section Rendering API URL
   * @param {string[]} selectedOptionValues - Array of selected option value IDs
   * @returns {string} The request URL
   */
  const buildSectionRenderingUrl = (selectedOptionValues) => {
    if (!currentProductUrl) return '';
    
    // Get the section ID from the product-info element
    const productInfo = contentContainer?.querySelector('product-info');
    const sectionId = productInfo?.dataset?.section || 'main-product';
    
    // Build URL with section_id and option_values parameters
    const url = new URL(currentProductUrl, window.location.origin);
    url.searchParams.set('section_id', sectionId);
    url.searchParams.set('option_values', selectedOptionValues.join(','));
    
    return url.toString();
  };

  /**
   * Fetches the section and updates the content
   * @param {string} requestUrl - The Section Rendering API URL
   */
  const fetchAndUpdateSection = async (requestUrl) => {
    // Abort previous request if still pending
    if (abortController) {
      abortController.abort();
    }
    abortController = new AbortController();
    
    try {
      const response = await fetch(requestUrl, {
        signal: abortController.signal
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error: ${response.status}`);
      }
      
      const html = await response.text();
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');

      // Update the content from the fetched section
      updateFromSection(doc);
      
    } catch (error) {
      if (error.name !== 'AbortError') {
        console.error('Error fetching section:', error);
        
        // Remove loading state on error
        const submitButton = contentContainer?.querySelector('.product-form__submit');
        if (submitButton) {
          submitButton.classList.remove('loading');
        }
      }
    }
  };

  /**
   * Updates the quick view content from the fetched section
   * @param {Document} doc - The parsed HTML document from Section Rendering API
   */
  const updateFromSection = (doc) => {
    if (!contentContainer) return;
    
    // Update variant selects (dropdowns/swatches) - this is crucial for updating available options
    updateVariantSelects(doc);
    
    // Update price
    updatePrice(doc);
    
    // Update variant ID input
    updateVariantId(doc);
    
    // Update button state
    updateButton(doc);
    
    // Update SKU if present
    updateElement(doc, '[id^="Sku-"]');
    
    // Update inventory if present
    updateElement(doc, '[id^="Inventory-"]');
  };

  /**
   * Updates the price from the fetched section
   * @param {Document} doc - The parsed HTML document
   */
  const updatePrice = (doc) => {
    const sourcePrice = doc.querySelector('.price');
    const targetPrice = contentContainer?.querySelector('.price');
    
    if (sourcePrice && targetPrice) {
      targetPrice.innerHTML = sourcePrice.innerHTML;
      targetPrice.className = sourcePrice.className;
    }
  };

  /**
   * Updates the variant selects (dropdowns/swatches) from the fetched section
   * This is crucial for updating available options when a variant changes
   * @param {Document} doc - The parsed HTML document
   */
  const updateVariantSelects = (doc) => {
    const sourceVariantSelects = doc.querySelector('variant-selects');
    const targetVariantSelects = contentContainer?.querySelector('variant-selects');
    
    if (!sourceVariantSelects || !targetVariantSelects) return;

    // Get all selects from both source and target
    const sourceSelects = sourceVariantSelects.querySelectorAll('select');
    const targetSelects = targetVariantSelects.querySelectorAll('select');

    // Update each select's options while preserving selections where possible
    sourceSelects.forEach((sourceSelect, index) => {
      const targetSelect = targetSelects[index];
      if (!targetSelect) return;

      // Store the current width to prevent layout shift
      const currentWidth = targetSelect.offsetWidth;
      const selectWrapper = targetSelect.closest('.select');
      if (selectWrapper && currentWidth) {
        selectWrapper.style.width = `${currentWidth}px`;
      }

      // Get the currently selected value
      const currentlySelected = targetSelect.options[targetSelect.selectedIndex];
      const currentValue = currentlySelected?.value;
      const currentOptionValueId = currentlySelected?.dataset.optionValueId;

      // Remove existing event listener before replacing HTML (prevent duplicates)
      targetSelect.removeEventListener('change', handleVariantChange);

      // Replace the select's innerHTML with new options
      targetSelect.innerHTML = sourceSelect.innerHTML;

      // Try to restore the previously selected value
      if (currentOptionValueId) {
        const optionToSelect = Array.from(targetSelect.options).find(
          opt => opt.dataset.optionValueId === currentOptionValueId
        );
        if (optionToSelect) {
          optionToSelect.selected = true;
        }
      } else if (currentValue) {
        const optionToSelect = Array.from(targetSelect.options).find(
          opt => opt.value === currentValue
        );
        if (optionToSelect) {
          optionToSelect.selected = true;
        }
      }

      // Re-attach event listener
      targetSelect.addEventListener('change', handleVariantChange);

      // If no match found, the sourceSelect already has the right selection from the server
      // (it will have the first available option or the option based on the API response)
    });

    // Update radio buttons
    const sourceRadioGroups = sourceVariantSelects.querySelectorAll('fieldset');
    const targetRadioGroups = targetVariantSelects.querySelectorAll('fieldset');

    sourceRadioGroups.forEach((sourceFieldset, index) => {
      const targetFieldset = targetRadioGroups[index];
      if (!targetFieldset) return;

      // Get currently checked radio in target
      const currentlyChecked = targetFieldset.querySelector('input[type="radio"]:checked');
      const currentOptionValueId = currentlyChecked?.dataset.optionValueId;

      // Remove existing event listeners from radios
      const oldRadios = targetFieldset.querySelectorAll('input[type="radio"]');
      oldRadios.forEach(radio => {
        radio.removeEventListener('change', handleVariantChange);
      });

      // Replace fieldset content
      targetFieldset.innerHTML = sourceFieldset.innerHTML;

      // Try to restore the checked radio
      if (currentOptionValueId) {
        const radioToCheck = targetFieldset.querySelector(
          `input[type="radio"][data-option-value-id="${currentOptionValueId}"]`
        );
        if (radioToCheck) {
          radioToCheck.checked = true;
        }
      }

      // Re-attach event listeners to new radios
      const newRadios = targetFieldset.querySelectorAll('input[type="radio"]');
      newRadios.forEach(radio => {
        radio.addEventListener('change', handleVariantChange);
      });
    });
  };

  /**
   * Updates the variant ID input from the fetched section
   * @param {Document} doc - The parsed HTML document
   */
  const updateVariantId = (doc) => {
    const sourceInput = doc.querySelector('input[name="id"].product-variant-id');
    const targetInput = contentContainer?.querySelector('input[name="id"].product-variant-id');
    
    if (sourceInput && targetInput) {
      targetInput.value = sourceInput.value;
      targetInput.disabled = sourceInput.disabled;
    }
  };

  /**
   * Updates the add-to-cart button from the fetched section
   * @param {Document} doc - The parsed HTML document
   */
  const updateButton = (doc) => {
    const sourceButton = doc.querySelector('.product-form__submit');
    const targetButton = contentContainer?.querySelector('.product-form__submit');

    if (sourceButton && targetButton) {
      // Remove loading state first
      targetButton.classList.remove('loading');
      
      targetButton.disabled = sourceButton.disabled;
      targetButton.className = sourceButton.className;

      const sourceText = sourceButton.querySelector('span');
      const targetText = targetButton.querySelector('span');
      if (sourceText && targetText) {
        targetText.textContent = sourceText.textContent;
      }

      // Override: keep disabled if not all options have valid selections
      if (!areAllOptionsSelected()) {
        targetButton.disabled = true;
      }
    }
  };

  /**
   * Updates a generic element from the fetched section
   * @param {Document} doc - The parsed HTML document
   * @param {string} selector - The element selector
   */
  const updateElement = (doc, selector) => {
    const sourceElement = doc.querySelector(selector);
    const targetElement = contentContainer?.querySelector(selector);
    
    if (sourceElement && targetElement) {
      targetElement.innerHTML = sourceElement.innerHTML;
      targetElement.className = sourceElement.className;
    }
  };

  /**
   * Cleans up event listeners
   */
  const destroy = () => {
    if (abortController) {
      abortController.abort();
    }
    currentProductUrl = null;
    contentContainer = null;
  };

  return {
    init,
    destroy
  };
})();
